import React from 'react';
import { X, ClipboardList, Hand, Footprints, Shapes, Wind, Spline, Brain, Droplets, Baby, Scissors, Smile, Ear, Bone, HeartHandshake, CircleDot, Flower2, Flame, Accessibility, CheckCircle2, Ban, MoveHorizontal, ChevronsUp, CircleDashed, Activity, ArrowDownCircle, ArrowUpCircle, Video as LucideIcon } from 'lucide-react';

interface VignetteConfig {
  id: string;
  label: string;
  Icon: LucideIcon;
  color: string;
}

interface VisualFilterVignettesProps {
  selectedVignettes: string[];
  onVignetteToggle: (vignette: string) => void;
  onClearAll: () => void;
  darkMode: boolean;
  availableVignettes?: string[];
}

const NEUTRAL = 'bg-[rgba(1,42,54,0.04)] text-appines-petrol border-[rgba(1,42,54,0.12)] hover:bg-[rgba(1,42,54,0.08)]';

const MAIN_VIGNETTES: VignetteConfig[] = [
  { id: 'Bilan', label: 'Bilan', Icon: ClipboardList, color: NEUTRAL },
  { id: 'Membres supérieurs', label: 'Membres supérieurs', Icon: Hand, color: NEUTRAL },
  { id: 'Membres inférieurs', label: 'Membres inférieurs', Icon: Footprints, color: NEUTRAL },
  { id: 'Autres', label: 'Autres', Icon: Shapes, color: NEUTRAL },
  { id: 'Respi', label: 'Respi', Icon: Wind, color: NEUTRAL },
  { id: 'Rachis', label: 'Rachis', Icon: Spline, color: NEUTRAL },
  { id: 'Neurologie', label: 'Neurologie', Icon: Brain, color: NEUTRAL },
  { id: 'Vasculaire - oedème', label: 'Vasculaire - oedème', Icon: Droplets, color: NEUTRAL },
  { id: 'Pédiatrie', label: 'Pédiatrie', Icon: Baby, color: NEUTRAL },
  { id: 'Amputation', label: 'Amputation', Icon: Scissors, color: NEUTRAL },
  { id: 'Maxillo-facial', label: 'Maxillo-facial', Icon: Smile, color: NEUTRAL },
  { id: 'Vestibulaire', label: 'Vestibulaire', Icon: Ear, color: NEUTRAL },
  { id: 'Rhumathologie', label: 'Rhumathologie', Icon: Bone, color: NEUTRAL },
  { id: 'Soins palliatifs', label: 'Soins palliatifs', Icon: HeartHandshake, color: NEUTRAL },
  { id: 'Abdomen', label: 'Abdomen', Icon: CircleDot, color: NEUTRAL },
  { id: 'Uro-gynéco', label: 'Uro-gynéco', Icon: Flower2, color: NEUTRAL },
  { id: 'Brûlures', label: 'Brûlures', Icon: Flame, color: NEUTRAL },
  { id: 'Gériatrie', label: 'Gériatrie', Icon: Accessibility, color: NEUTRAL },
  { id: 'Opéré', label: 'Opéré', Icon: CheckCircle2, color: 'bg-appines-glass/25 text-appines-petrol border-appines-glass/50 hover:bg-appines-glass/40' },
  { id: 'Non-opéré', label: 'Non-opéré', Icon: Ban, color: 'bg-appines-petal/20 text-appines-petrol border-appines-petal/40 hover:bg-appines-petal/30' },
];

const SUB_GLASS = 'bg-appines-glass/20 text-appines-petrol border-appines-glass/40 hover:bg-appines-glass/30';

const SUB_VIGNETTES: Record<string, VignetteConfig[]> = {
  'Membres supérieurs': [
    { id: 'Coude', label: 'Coude', Icon: MoveHorizontal, color: SUB_GLASS },
    { id: 'Epaule', label: 'Épaule', Icon: ChevronsUp, color: SUB_GLASS },
    { id: 'Poignet', label: 'Poignet', Icon: CircleDashed, color: SUB_GLASS },
  ],
  'Membres inférieurs': [
    { id: 'Pied', label: 'Pied', Icon: Footprints, color: SUB_GLASS },
    { id: 'Cheville', label: 'Cheville', Icon: Activity, color: SUB_GLASS },
    { id: 'Genou', label: 'Genou', Icon: ArrowDownCircle, color: SUB_GLASS },
    { id: 'Hanche', label: 'Hanche', Icon: ArrowUpCircle, color: SUB_GLASS },
  ],
};

const VisualFilterVignettes: React.FC<VisualFilterVignettesProps> = ({
  selectedVignettes,
  onVignetteToggle,
  onClearAll,
  darkMode,
  availableVignettes = [],
}) => {
  const hasSelectedVignettes = selectedVignettes.length > 0;

  const isMainCategorySelected = (categoryId: string) => selectedVignettes.includes(categoryId);

  const visibleSubVignettes: Array<VignetteConfig & { parentCategory: string }> = [];
  Object.entries(SUB_VIGNETTES).forEach(([parentCategory, subVignettes]) => {
    if (isMainCategorySelected(parentCategory)) {
      subVignettes.forEach((subVignette) => {
        visibleSubVignettes.push({ ...subVignette, parentCategory });
      });
    }
  });

  return (
    <div className="max-w-4xl mx-auto mt-6 mb-4">
      <div className="hidden flex items-center justify-between mb-4">
        <h3 className={`text-sm font-medium ${darkMode ? 'text-appines-white/65' : 'text-appines-petrol/65'}`}>
          Filtres rapides
        </h3>
        {hasSelectedVignettes && (
          <button
            onClick={onClearAll}
            className={`flex items-center text-xs px-2 py-1 rounded-md transition-colors ${
              darkMode
                ? 'text-appines-white/45 hover:text-appines-white hover:bg-appines-white/10'
                : 'text-appines-petrol/65 hover:text-appines-petrol hover:bg-[rgba(1,42,54,0.06)]'
            }`}
            aria-label="Effacer tous les filtres visuels"
          >
            <X className="h-3 w-3 mr-1" />
            Effacer ({selectedVignettes.length})
          </button>
        )}
      </div>

      <div className="space-y-2 sm:space-y-3">
        <div className="flex flex-wrap gap-1.5 sm:gap-2 justify-start sm:justify-center px-2">
          {MAIN_VIGNETTES.filter((vignette) => {
            if (vignette.id === 'Membres supérieurs' && selectedVignettes.includes('Membres inférieurs')) {
              return false;
            }
            if (vignette.id === 'Membres inférieurs' && selectedVignettes.includes('Membres supérieurs')) {
              return false;
            }
            const isSelected = selectedVignettes.includes(vignette.id);
            const isAvailable = availableVignettes.length === 0 || availableVignettes.includes(vignette.id);
            return isSelected || isAvailable;
          }).map((vignette) => {
            const isSelected = selectedVignettes.includes(vignette.id);
            const isAvailable = availableVignettes.length === 0 || availableVignettes.includes(vignette.id);
            const isDisabled = !isSelected && !isAvailable;
            const Icon = vignette.Icon;

            return (
              <button
                key={vignette.id}
                onClick={() => onVignetteToggle(vignette.id)}
                disabled={isDisabled}
                className={`
                  flex items-center gap-2 sm:gap-3 px-2.5 py-1.5 sm:px-4 sm:py-3 rounded-lg border-2 text-xs sm:text-sm font-medium
                  transition-all duration-200 transform ${!isDisabled ? 'hover:scale-105 active:scale-95' : ''}
                  ${
                    isSelected
                      ? 'bg-appines-emerald text-appines-petrol border-appines-emerald shadow-lg'
                      : isDisabled
                        ? darkMode
                          ? 'bg-appines-petrol text-appines-white/35 border-appines-white/10 opacity-50 cursor-not-allowed'
                          : 'bg-[rgba(1,42,54,0.04)] text-appines-petrol/35 border-[rgba(1,42,54,0.1)] opacity-50 cursor-not-allowed'
                        : darkMode
                          ? 'bg-appines-petrol text-appines-white/65 border-appines-white/20 hover:bg-appines-white/10 hover:text-appines-white hover:border-appines-white/30'
                          : `${vignette.color} border-2`
                  }
                `}
                aria-label={`${isSelected ? 'Désactiver' : 'Activer'} le filtre ${vignette.label}`}
                aria-pressed={isSelected}
                title={isDisabled ? `Aucun résultat disponible pour ${vignette.label}` : undefined}
              >
                <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${isDisabled ? 'opacity-60' : ''}`} aria-hidden="true" />
                <span className="whitespace-nowrap">{vignette.label}</span>
                {isSelected && (
                  <div
                    className="w-2 h-2 rounded-full bg-appines-petrol/30"
                    aria-hidden="true"
                  />
                )}
              </button>
            );
          })}
        </div>

        {visibleSubVignettes.length > 0 && (
          <div className="flex flex-wrap gap-1.5 sm:gap-2 justify-start sm:justify-center px-2 sm:px-6">
            {visibleSubVignettes.filter((subVignette) => {
              const isSelected = selectedVignettes.includes(subVignette.id);
              const isAvailable = availableVignettes.length === 0 || availableVignettes.includes(subVignette.id);
              return isSelected || isAvailable;
            }).map((subVignette) => {
              const isSelected = selectedVignettes.includes(subVignette.id);
              const isAvailable = availableVignettes.length === 0 || availableVignettes.includes(subVignette.id);
              const isDisabled = !isSelected && !isAvailable;
              const Icon = subVignette.Icon;

              return (
                <button
                  key={subVignette.id}
                  onClick={() => onVignetteToggle(subVignette.id)}
                  disabled={isDisabled}
                  className={`
                    flex items-center gap-2 sm:gap-3 px-2.5 py-1.5 sm:px-4 sm:py-3 rounded-lg border-2 text-xs sm:text-sm font-medium
                    transition-all duration-200 transform ${!isDisabled ? 'hover:scale-105 active:scale-95' : ''}
                    ${
                      isSelected
                        ? 'bg-appines-emerald text-appines-petrol border-appines-emerald shadow-lg'
                        : isDisabled
                          ? darkMode
                            ? 'bg-appines-petrol text-appines-white/35 border-appines-white/10 opacity-50 cursor-not-allowed'
                            : 'bg-[rgba(1,42,54,0.04)] text-appines-petrol/35 border-[rgba(1,42,54,0.1)] opacity-50 cursor-not-allowed'
                          : darkMode
                            ? 'bg-appines-petrol text-appines-white/65 border-appines-white/20 hover:bg-appines-white/10 hover:text-appines-white hover:border-appines-white/30'
                            : `${subVignette.color} border-2`
                    }
                  `}
                  aria-label={`${isSelected ? 'Désactiver' : 'Activer'} le filtre ${subVignette.label}`}
                  aria-pressed={isSelected}
                  title={isDisabled ? `Aucun résultat disponible pour ${subVignette.label}` : undefined}
                >
                  <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${isDisabled ? 'opacity-60' : ''}`} aria-hidden="true" />
                  <span className="whitespace-nowrap">{subVignette.label}</span>
                  {isSelected && (
                    <div
                      className="w-2 h-2 rounded-full bg-appines-petrol/30"
                      aria-hidden="true"
                    />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {hasSelectedVignettes && (
        <div className={`hidden mt-3 text-center text-xs ${darkMode ? 'text-appines-white/45' : 'text-appines-petrol/65'}`}>
          {selectedVignettes.length} filtre{selectedVignettes.length > 1 ? 's' : ''} actif
          {selectedVignettes.length > 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
};

export default VisualFilterVignettes;
